import type { CardRow, DeckProject, DeckVariable, ProjectAsset } from './types'

export interface GridCell {
  row: number
  column: number
}

export interface GridRange {
  anchor: GridCell
  focus: GridCell
}

export interface NormalizedGridRange {
  top: number
  bottom: number
  left: number
  right: number
}

export type GridFillDirection = 'down' | 'right'

export type GridEditKeyAction = 'native' | 'cancel' | 'commit-up' | 'commit-down' | 'commit-previous' | 'commit-next'

export const gridEditKeyAction = (key: string, shiftKey = false): GridEditKeyAction => {
  if (key === 'Escape') return 'cancel'
  if (key === 'Enter') return shiftKey ? 'commit-up' : 'commit-down'
  if (key === 'Tab') return shiftKey ? 'commit-previous' : 'commit-next'
  return 'native'
}

export const normalizeGridRange = (range: GridRange): NormalizedGridRange => ({
  top: Math.min(range.anchor.row, range.focus.row),
  bottom: Math.max(range.anchor.row, range.focus.row),
  left: Math.min(range.anchor.column, range.focus.column),
  right: Math.max(range.anchor.column, range.focus.column),
})

export const clampGridCell = (cell: GridCell, rowCount: number, columnCount: number): GridCell => ({
  row: Math.max(0, Math.min(Math.max(0, rowCount - 1), cell.row)),
  column: Math.max(0, Math.min(Math.max(0, columnCount - 1), cell.column)),
})

export const clampGridRange = (range: GridRange, rowCount: number, columnCount: number): GridRange => ({
  anchor: clampGridCell(range.anchor, rowCount, columnCount),
  focus: clampGridCell(range.focus, rowCount, columnCount),
})

export const gridRangeSize = (range: GridRange) => {
  const normalized = normalizeGridRange(range)
  const rows = normalized.bottom - normalized.top + 1
  const columns = normalized.right - normalized.left + 1
  return { rows, columns, cells: rows * columns }
}

export const gridCellInRange = (cell: GridCell, range: GridRange) => {
  const normalized = normalizeGridRange(range)
  return cell.row >= normalized.top && cell.row <= normalized.bottom && cell.column >= normalized.left && cell.column <= normalized.right
}

export const gridCellValue = (project: DeckProject, row: CardRow, column: number) => {
  if (column === 0) return row.name
  const variable = project.variables[column - 1]
  return variable ? row.values[variable.id] ?? variable.defaultValue : ''
}

export const emptyGridValue = (variable: DeckVariable) => {
  if (variable.type === 'number' || variable.type === 'boolean') return 0
  if (variable.type === 'color') return '#000000'
  return ''
}

export const coerceGridValue = (
  value: string | number,
  variable: DeckVariable,
  assets: Record<string, ProjectAsset>,
) => {
  if (variable.type === 'number') {
    const number = Number(value)
    return Number.isFinite(number) ? number : 0
  }
  if (variable.type === 'boolean') return /^(true|yes|1)$/i.test(String(value).trim()) ? 1 : 0
  if (variable.type === 'image') {
    const supplied = String(value).trim()
    if (assets[supplied]) return supplied
    return Object.values(assets).find((asset) => asset.name.toLocaleLowerCase() === supplied.toLocaleLowerCase())?.id ?? ''
  }
  return String(value)
}

const clipboardValue = (project: DeckProject, row: CardRow, column: number) => {
  const value = gridCellValue(project, row, column)
  const variable = project.variables[column - 1]
  if (variable?.type === 'boolean') return Number(value) ? 'TRUE' : 'FALSE'
  if (variable?.type === 'image') return project.assets[String(value)]?.name ?? ''
  return String(value)
}

const escapeClipboardCell = (value: string) =>
  /[\t\r\n"]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value

export const gridRangeToClipboardText = (project: DeckProject, range: GridRange) => {
  if (!project.rows.length) return ''
  const clamped = clampGridRange(range, project.rows.length, project.variables.length + 1)
  const normalized = normalizeGridRange(clamped)
  return project.rows.slice(normalized.top, normalized.bottom + 1)
    .map((row) => Array.from({ length: normalized.right - normalized.left + 1 }, (_, offset) => escapeClipboardCell(clipboardValue(project, row, normalized.left + offset))).join('\t'))
    .join('\r\n')
}

export const isSingleNumberColumn = (project: DeckProject, range: GridRange) => {
  const normalized = normalizeGridRange(range)
  return normalized.left === normalized.right && normalized.left > 0 && project.variables[normalized.left - 1]?.type === 'number'
}
